import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button } from "./Button";
import { FieldErrorsProvider } from "./FieldErrors";
import { Icon } from "./Icon";

export const DialogRoot = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  /** doc 41 §6: say what happened and what to do. */
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** `md` for most dialogs; `lg` for evidence and side-by-side comparison. */
  size?: "sm" | "md" | "lg";
}

const SIZE = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-3xl" } as const;

/** Shared by Dialog and Drawer so the scrim is defined once (doc 40 §3). */
export function Scrim() {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-modal bg-scrim",
        "data-[state=open]:animate-fade-in motion-reduce:animate-none",
      )}
    />
  );
}

/**
 * Modal dialog. Radix owns focus trap, focus restore and Escape (doc 27 §9);
 * we own the shape: (H) doc 40 §7 — modals are `radius-xl` (12px) — and the
 * elevation: doc 40 §6 `shadow-modal`, which already carries its own hairline
 * ring, so there is no `border` here.
 *
 * ── (H) The dialog is centred by a flex wrapper, never by `transform` ────────
 *
 * It used to centre itself with `left-1/2 top-1/2 -translate-x-1/2
 * -translate-y-1/2`, which is the usual Radix recipe — and it was broken,
 * because `scale-in`'s keyframes set `transform`, and a running animation's
 * `transform` **replaces** the element's own. For the animation's whole
 * duration the translate simply did not exist, so the dialog rendered with its
 * top-left corner at the centre point — offset by half its own size — and then
 * snapped into place at the last frame. Measured on a 512×630 dialog in a
 * 1440×900 viewport: it opened **262px right and 323px low** and slid up-left
 * into position, which reads exactly as "it drags up from somewhere below".
 *
 * A flex wrapper decouples the two permanently: position comes from layout, and
 * the only `transform` on the content is the animation's own scale. Do not
 * reintroduce transform-based centring here, and do not add a `transform` to
 * any keyframe used by a transform-positioned element.
 *
 * The wrapper is `pointer-events-none` so that clicking beside the dialog still
 * reaches the scrim underneath.
 *
 * ── (H) A click outside never closes it (ticket 8602) ──────────────────────
 *
 * A stray click on the scrim used to close the dialog and throw away whatever
 * had been typed — a payer's details, a payment, a note. A dialog closes on its
 * Cancel, its ×, or Escape, which are deliberate; a form dialog then asks before
 * discarding typed input (`FormDialog`).
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
}: DialogProps) {
  return (
    <DialogPrimitive.Root
      {...(open === undefined ? {} : { open })}
      {...(onOpenChange === undefined ? {} : { onOpenChange })}
    >
      <DialogPrimitive.Portal>
        <Scrim />
        <div className="pointer-events-none fixed inset-0 z-modal flex items-center justify-center p-4">
          <DialogPrimitive.Content
            onInteractOutside={(event) => event.preventDefault()}
            className={cn(
              "pointer-events-auto w-full",
              "flex max-h-[90vh] flex-col overflow-hidden rounded-xl bg-default shadow-modal",
              "data-[state=open]:animate-scale-in motion-reduce:animate-none",
              SIZE[size],
            )}
          >
            <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-6">
              <div className="min-w-0">
                <DialogPrimitive.Title className="text-xl text-default">
                  {title}
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description className="mt-1 text-sm text-tertiary">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="sm" iconOnly aria-label="Close">
                  <Icon name="close" size={4} />
                </Button>
              </DialogPrimitive.Close>
            </div>
            {children ? (
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                {/* D239: a dialog's fields show the server's messages for them. */}
                <FieldErrorsProvider>{children}</FieldErrorsProvider>
              </div>
            ) : null}
            {footer ? (
              <div className="flex items-center justify-end gap-2 border-t border-weak bg-canvas px-6 py-3.5">
                {footer}
              </div>
            ) : null}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
